import React from 'react';
import { createFragmentContainer } from 'react-relay';
import { graphql } from 'babel-plugin-relay/macro';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { TranslationAnswerDetails_answer } from './__generated__/TranslationAnswerDetails_answer.graphql';
import { findPiecesSpanningText } from '../../lib/atomHelpers';
import './TranslationAnswerDetails.css';

type Props = {
  answer: TranslationAnswerDetails_answer;
};

const renderWordParts = (parts: readonly string[], isCorrect: boolean) => (
  <div
    className={classNames('TranslationAnswerDetails-wordParts br1 ph2 pt2', {
      'is-correct': isCorrect,
      'is-incorrect': !isCorrect,
    })}
  >
    {parts.map((part, i: number) => (
      <div
        key={i}
        className={classNames('bg-white ph2 pv1 fl mb2', {
          mr2: i !== parts.length - 1,
        })}
      >
        {part}
      </div>
    ))}
    <div className="cb" />
  </div>
);

const TranslationAnswerDetails = ({ answer }: Props) => {
  let correctAnswers: readonly string[] = [];
  let scrambledParts: readonly string[] = [];
  let prompt; // need to do it this way to keep flow happy :/

  if (answer.exercise && answer.exercise.content) {
    correctAnswers = answer.exercise.content.correctAnswers;
    scrambledParts = answer.exercise.content.scrambledParts;
    prompt = answer.exercise.content.prompt;
  }

  const correctAnswerPiecesOrder =
    findPiecesSpanningText(correctAnswers[0], scrambledParts.slice()) || [];
  const correctAnswerPieces = correctAnswerPiecesOrder.map(
    index => scrambledParts[index],
  );
  return (
    <div className="TranslationAnswerDetails">
      <div className="TranslationAnswerDetails-prompt">{prompt}</div>
      <div className="TranslationAnswerDetails-responses">
        <div className="cb" />
        <div className="TranslationAnswerDetails-response fl mr2 mt3">
          <div className="gray f7 mb1">
            <FormattedMessage
              id="TranslationAnswerDetails.user_response"
              defaultMessage="Your response"
            />
          </div>
          {renderWordParts(answer.response || [], answer.isCorrect || false)}
        </div>
        {answer.isCorrect ? null : (
          <div className="TranslationAnswerDetails-response fl mt3">
            <div className="gray f7 mb1">
              <FormattedMessage
                id="TranslationAnswerDetails.correct_answer"
                defaultMessage="Correct answer"
              />
            </div>
            {renderWordParts(correctAnswerPieces || [], true)}
          </div>
        )}
        <div className="cb" />
      </div>
    </div>
  );
};

export default createFragmentContainer(TranslationAnswerDetails, {
  answer: graphql`
    fragment TranslationAnswerDetails_answer on TranslationAnswer {
      isCorrect
      response
      exercise {
        ... on TranslationExercise {
          content {
            prompt
            correctAnswers
            scrambledParts
          }
        }
      }
    }
  `,
});
